import { CheckCircle, Close, RadioButtonUnchecked, SwapVert } from "@mui/icons-material"
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useTheme,
    type SxProps,
} from "@mui/material"
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import type { IOpportunity } from "src/services/api/opportunities/types"
import {
    buildAdvisorAggregatedModel,
    type AdvisorAggregate,
    type AdvisorOpportunitySlice,
    type CarteiraComposition,
} from "src/parts/OpportunitiesSection/aggregateOpportunities"
import type { Filter } from "../../basics/TabFilters/types"
import { BoxCard, Grid, IconButton, TabFilters, Typography } from "../../basics"
import { barColorForRatio, formatPercent } from "../ObjectiveMonitoringPanel/AdvisorExecutionChart"
import {
    getMonitoringTableContainerSx,
    getMonitoringTableSx,
} from "../ObjectiveMonitoringPanel/monitoringTableStyles"

export interface AggregatedOpportunitiesChartProps {
    objectives: IOpportunity[]
    onClose?: () => void
}

type SortDirection = "desc" | "asc"
type ViewMode = "chart" | "table"

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n))

/** Participação mínima do segmento para exibir o rótulo de % executado. */
const LABEL_THRESHOLD = 0.1

const advisorDisplay = (label: string): string => label || intl("aggOpportunitiesNoAdvisor")

const formatClientsDone = (done: number, total: number): string =>
    intl("objectiveMonitoringClientsDoneShort", {
        values: { done: String(done), total: String(total) },
    })

const isClientExecuted = (ratio: number | null): boolean =>
    ratio != null && Number.isFinite(ratio) && ratio > 0

const clientStatusColor = (done: boolean): string =>
    done ? "accentSuccess.main" : "accentError.main"

const formatSharePrecise = (share: number): string =>
    new Intl.NumberFormat("pt-BR", {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(share)

const sortAdvisors = (rows: AdvisorAggregate[], direction: SortDirection): AdvisorAggregate[] => {
    const copy = [...rows]
    copy.sort((a, b) => {
        const na = a.aggregateRatio === null
        const nb = b.aggregateRatio === null
        if (na && nb) return advisorDisplay(a.advisorLabel).localeCompare(advisorDisplay(b.advisorLabel), "pt-BR")
        if (na) return 1
        if (nb) return -1
        const cmp = a.aggregateRatio! - b.aggregateRatio!
        return direction === "desc" ? -cmp : cmp
    })
    return copy
}

/* ------------------------------------------------------------------ */
/* Tooltips                                                            */
/* ------------------------------------------------------------------ */

const SliceTooltip = ({ slice, total }: { slice: AdvisorOpportunitySlice; total: number }) => {
    const weight = total > 0 ? slice.potential / total : 0
    const contribution = total > 0 ? clamp01(slice.executed / total) : 0
    return (
        <Box sx={{ p: 0.5, maxWidth: 280 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                {slice.oppTitle}
            </Typography>
            <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                {`${intl("aggOpportunitiesPotentialLabel")}: ${formatCurrencyBRL(slice.potential)} · ${formatSharePrecise(weight)} ${intl("aggOpportunitiesShareSuffix")}`}
            </Typography>
            <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                {`${intl("aggOpportunitiesExecutedLabel")}: ${formatCurrencyBRL(slice.executed)} · ${formatPercent(slice.ratio)}`}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "neutral.lighter" }}>
                {`${formatPercent(contribution)} ${intl("aggOpportunitiesGlobalProgressSuffix")}`}
            </Typography>
        </Box>
    )
}

/* ------------------------------------------------------------------ */
/* Barra única de progresso global                                     */
/* Cada fatia = contribuição da oportunidade (peso × execução =        */
/* executado / total do assessor). A soma das fatias = % médio global. */
/* ------------------------------------------------------------------ */

interface MarimekkoBarProps {
    advisor: AdvisorAggregate
    opportunityColors: Map<string, string>
    height?: number
}

const MarimekkoBar = ({ advisor, opportunityColors, height = 26 }: MarimekkoBarProps) => {
    const total = advisor.totalPotential
    const slices = advisor.slices.filter((s) => s.potential > 0)

    if (total <= 0 || slices.length === 0) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height,
                    borderRadius: 1,
                    bgcolor: "neutral.darkest",
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                }}
            />
        )
    }

    return (
        <Box
            sx={{
                display: "flex",
                width: "100%",
                height,
                borderRadius: 1,
                overflow: "hidden",
                // O espaço não preenchido (até 100%) = progresso global restante.
                bgcolor: "neutral.darkest",
                border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
        >
            {slices.map((slice) => {
                // Fatia = contribuição da oportunidade ao progresso global.
                const contribution = clamp01(slice.executed / total)
                const widthPct = contribution * 100
                if (widthPct <= 0) return null
                const color = opportunityColors.get(slice.oppKey) ?? "#80AFD1"
                return (
                    <Tooltip
                        key={slice.oppKey}
                        arrow
                        placement="top"
                        title={<SliceTooltip slice={slice} total={total} />}
                    >
                        <Box
                            sx={{
                                width: `${widthPct}%`,
                                flexShrink: 0,
                                height: "100%",
                                bgcolor: color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                cursor: "inherit",
                                transition: "width 240ms ease",
                            }}
                        >
                            {widthPct >= LABEL_THRESHOLD * 100 && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#FFFFFF",
                                        bgcolor: "rgba(10, 11, 12, 0.5)",
                                        px: 0.5,
                                        borderRadius: 0.5,
                                        lineHeight: 1.4,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {formatPercent(contribution)}
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                )
            })}
        </Box>
    )
}

/* ------------------------------------------------------------------ */
/* ESQUERDA — assessores (gráfico)                                     */
/* ------------------------------------------------------------------ */

interface AdvisorChartProps {
    rows: AdvisorAggregate[]
    opportunityColors: Map<string, string>
    selectedKeys: string[]
    onToggle: (advisorKey: string) => void
}

const AdvisorAggregateChart = ({ rows, opportunityColors, selectedKeys, onToggle }: AdvisorChartProps) => {
    if (!rows.length) {
        return (
            <Typography variant="caption" color="quaternary">
                {intl("objectiveMonitoringChartEmpty")}
            </Typography>
        )
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((row) => {
                const selected = selectedKeys.includes(row.advisorKey)
                return (
                    <Box
                        key={row.advisorKey}
                        onClick={() => onToggle(row.advisorKey)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            px: 0.75,
                            py: 0.5,
                            borderRadius: 1,
                            borderLeft: "3px solid",
                            borderLeftColor: selected ? "primary.main" : "transparent",
                            bgcolor: selected ? "neutral.darker" : "transparent",
                            transition: "background-color 120ms ease",
                            "&:hover": { bgcolor: "neutral.darker" },
                        }}
                    >
                        {selected ? (
                            <CheckCircle sx={{ fontSize: 16, color: "primary.main", flexShrink: 0 }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: 16, color: "neutral.dark", flexShrink: 0 }} />
                        )}
                        <Box
                            sx={{
                                width: 132,
                                flexShrink: 0,
                                overflow: "hidden",
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {advisorDisplay(row.advisorLabel)}
                            </Typography>
                            <Typography variant="caption" color="quaternary" noWrap>
                                {formatClientsDone(row.clientsDone, row.clientsTotal)}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 80 }}>
                            <MarimekkoBar advisor={row} opportunityColors={opportunityColors} />
                        </Box>
                        <Box sx={{ width: 56, flexShrink: 0, textAlign: "right" }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: barColorForRatio(row.aggregateRatio) }}
                            >
                                {formatPercent(row.aggregateRatio)}
                            </Typography>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

/* ------------------------------------------------------------------ */
/* ESQUERDA — assessores (tabela)                                      */
/* ------------------------------------------------------------------ */

const headerCell = (label: string, align: "left" | "right" = "left") => (
    <TableCell align={align} sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="caption" component="span" sx={{ color: "neutral.lighter", fontWeight: 700 }}>
            {label}
        </Typography>
    </TableCell>
)

const bodyCell = (content: ReactNode, align: "left" | "right" = "left", maxWidth?: number) => (
    <TableCell align={align} sx={{ whiteSpace: "nowrap", maxWidth }}>
        <Typography variant="body2" component="span">
            {content}
        </Typography>
    </TableCell>
)

interface AdvisorTableProps {
    rows: AdvisorAggregate[]
    selectedKeys: string[]
    onToggle: (advisorKey: string) => void
    tableSx: SxProps
    tableContainerSx: SxProps
}

const AdvisorAggregateTable = ({ rows, selectedKeys, onToggle, tableSx, tableContainerSx }: AdvisorTableProps) => {
    if (!rows.length) {
        return (
            <Typography variant="caption" color="quaternary">
                {intl("objectiveMonitoringChartEmpty")}
            </Typography>
        )
    }
    return (
        <TableContainer sx={tableContainerSx}>
            <Table size="small" sx={tableSx}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 36 }} />
                        {headerCell(intl("objectiveMonitoringAdvisorCol"))}
                        {headerCell(intl("objectiveMonitoringClientsCol"), "right")}
                        {headerCell(intl("aggOpportunitiesPotentialLabel"), "right")}
                        {headerCell(intl("aggOpportunitiesExecutedLabel"), "right")}
                        {headerCell(intl("aggOpportunitiesAvgCol"), "right")}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const selected = selectedKeys.includes(row.advisorKey)
                        return (
                            <TableRow
                                key={row.advisorKey}
                                hover
                                onClick={() => onToggle(row.advisorKey)}
                                sx={{
                                    cursor: "pointer",
                                    "& .MuiTableCell-root": selected
                                        ? { backgroundColor: "neutral.darker !important" }
                                        : undefined,
                                }}
                            >
                                <TableCell sx={{ width: 36 }}>
                                    {selected ? (
                                        <CheckCircle sx={{ fontSize: 18, color: "primary.main", display: "block" }} />
                                    ) : (
                                        <RadioButtonUnchecked sx={{ fontSize: 18, color: "neutral.dark", display: "block" }} />
                                    )}
                                </TableCell>
                                {bodyCell(advisorDisplay(row.advisorLabel), "left", 200)}
                                {bodyCell(formatClientsDone(row.clientsDone, row.clientsTotal), "right")}
                                {bodyCell(formatCurrencyBRL(row.totalPotential), "right")}
                                {bodyCell(formatCurrencyBRL(row.totalExecuted), "right")}
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{ fontWeight: 700, color: barColorForRatio(row.aggregateRatio) }}
                                    >
                                        {formatPercent(row.aggregateRatio)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

/* ------------------------------------------------------------------ */
/* DIREITA — drill-down: oportunidades → carteiras do assessor          */
/* ------------------------------------------------------------------ */

const CarteiraBar = ({ carteira }: { carteira: CarteiraComposition }) => {
    const ratio = carteira.executionRatio
    const fillPct = ratio === null ? 0 : clamp01(ratio) * 100
    const done = isClientExecuted(ratio)
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
            <Box sx={{ width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <Typography variant="caption" noWrap title={carteira.label}>
                    {carteira.label}
                </Typography>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    width: 72,
                    flexShrink: 0,
                    fontWeight: 700,
                    color: clientStatusColor(done),
                    textAlign: "center",
                }}
            >
                {done ? intl("objectiveMonitoringClientDone") : intl("objectiveMonitoringClientNotDone")}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 48,
                    height: 14,
                    borderRadius: 1,
                    bgcolor: "neutral.darkest",
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ width: `${fillPct}%`, height: "100%", bgcolor: barColorForRatio(ratio) }} />
            </Box>
            <Box sx={{ width: 54, flexShrink: 0, textAlign: "right" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: barColorForRatio(ratio) }}>
                    {formatPercent(ratio)}
                </Typography>
            </Box>
        </Box>
    )
}

interface DrilldownProps {
    advisors: AdvisorAggregate[]
    opportunityColors: Map<string, string>
    viewMode: ViewMode
    tableSx: SxProps
    tableContainerSx: SxProps
}

const AdvisorAggregateDrilldown = ({ advisors, opportunityColors, viewMode, tableSx, tableContainerSx }: DrilldownProps) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {advisors.map((advisor) => (
            <Box key={advisor.advisorKey}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 1,
                        mb: 0.75,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {advisorDisplay(advisor.advisorLabel)}
                        </Typography>
                        <Typography variant="caption" color="quaternary">
                            {formatClientsDone(advisor.clientsDone, advisor.clientsTotal)}
                        </Typography>
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: barColorForRatio(advisor.aggregateRatio), flexShrink: 0 }}
                    >
                        {formatPercent(advisor.aggregateRatio)}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {advisor.slices.map((slice) => {
                        const color = opportunityColors.get(slice.oppKey) ?? "#80AFD1"
                        return (
                            <Box key={slice.oppKey}>
                                {/* Cabeçalho da oportunidade */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        mb: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }}
                                    />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap title={slice.oppTitle}>
                                            {slice.oppTitle}
                                        </Typography>
                                        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                                            {formatClientsDone(slice.clientsDone, slice.clientsTotal)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 700, color: barColorForRatio(slice.ratio), flexShrink: 0 }}
                                    >
                                        {formatPercent(slice.ratio)}
                                    </Typography>
                                </Box>

                                {viewMode === "chart" ? (
                                    <Box sx={{ pl: 2 }}>
                                        {slice.carteiras.map((c, idx) => (
                                            <CarteiraBar key={`${c.account}-${idx}`} carteira={c} />
                                        ))}
                                    </Box>
                                ) : (
                                    <TableContainer sx={tableContainerSx}>
                                        <Table size="small" sx={tableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    {headerCell(intl("objectiveMonitoringPortfolioCol"))}
                                                    {headerCell(intl("objectiveMonitoringClientStatusCol"), "right")}
                                                    {headerCell(intl("aggOpportunitiesPotentialLabel"), "right")}
                                                    {headerCell(intl("aggOpportunitiesExecutedLabel"), "right")}
                                                    {headerCell(intl("objectiveMonitoringPercentDoneCol"), "right")}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {slice.carteiras.map((c, idx) => {
                                                    const done = isClientExecuted(c.executionRatio)
                                                    return (
                                                    <TableRow key={`${c.account}-${idx}`}>
                                                        {bodyCell(c.label, "left", 200)}
                                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: clientStatusColor(done),
                                                                }}
                                                            >
                                                                {done
                                                                    ? intl("objectiveMonitoringClientDone")
                                                                    : intl("objectiveMonitoringClientNotDone")}
                                                            </Typography>
                                                        </TableCell>
                                                        {bodyCell(formatCurrencyBRL(c.value), "right")}
                                                        {bodyCell(formatCurrencyBRL(c.executedValue), "right")}
                                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                sx={{ fontWeight: 700, color: barColorForRatio(c.executionRatio) }}
                                                            >
                                                                {formatPercent(c.executionRatio)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        ))}
    </Box>
)

/* ------------------------------------------------------------------ */
/* Componente principal                                                */
/* ------------------------------------------------------------------ */

const AggregatedOpportunitiesChart = ({ objectives, onClose }: AggregatedOpportunitiesChartProps) => {
    const theme = useTheme()
    const model = useMemo(() => buildAdvisorAggregatedModel(objectives), [objectives])

    const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
    const [viewMode, setViewMode] = useState<ViewMode>("chart")
    const [selectedAdvisors, setSelectedAdvisors] = useState<string[]>([])
    const didInitSelection = useRef(false)

    const sortedAdvisors = useMemo(
        () => sortAdvisors(model.advisors, sortDirection),
        [model.advisors, sortDirection]
    )

    useEffect(() => {
        if (didInitSelection.current || sortedAdvisors.length === 0) return
        didInitSelection.current = true
        setSelectedAdvisors([sortedAdvisors[0].advisorKey])
    }, [sortedAdvisors])

    const toggleAdvisor = (advisorKey: string) => {
        setSelectedAdvisors((prev) =>
            prev.includes(advisorKey) ? prev.filter((k) => k !== advisorKey) : [...prev, advisorKey]
        )
    }

    const selectedRows = useMemo(
        () => sortedAdvisors.filter((r) => selectedAdvisors.includes(r.advisorKey)),
        [sortedAdvisors, selectedAdvisors]
    )

    const tableSx = useMemo(() => getMonitoringTableSx(theme), [theme])
    const tableContainerSx = useMemo(() => getMonitoringTableContainerSx(theme), [theme])

    const viewFilters = useMemo<Filter[]>(
        () => [
            { id: "chart", label: intl("objectiveMonitoringViewChart"), value: "chart" },
            { id: "table", label: intl("objectiveMonitoringViewTable"), value: "table" },
        ],
        []
    )
    const selectedViewFilter = viewFilters.find((f) => f.id === viewMode) ?? viewFilters[0]

    return (
        <BoxCard sx={{ p: 2 }}>
            <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Grid item sx={{ pr: 1, flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="quaternary">
                        {intl("aggOpportunitiesBadge")}
                    </Typography>
                    <Typography variant="h3" sx={{ mt: 0.25, lineHeight: 1.25 }}>
                        {intl("aggOpportunitiesTitle")}
                    </Typography>
                    <Typography variant="caption" color="quaternary" sx={{ display: "block", mt: 0.25 }}>
                        {intl("aggOpportunitiesSubtitle", {
                            values: { count: String(model.opportunities.length) },
                        })}
                    </Typography>
                </Grid>
                {onClose ? (
                    <IconButton
                        size="small"
                        color="neutral"
                        variant="clean"
                        aria-label={intl("objectiveMonitorCollapse")}
                        onClick={onClose}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                ) : null}
            </Grid>

            {/* Legenda das oportunidades */}
            {model.opportunities.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, rowGap: 0.5, mb: 1.5 }}>
                    {model.opportunities.map((o) => (
                        <Box key={`legend-${o.key}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box
                                sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: o.color, flexShrink: 0 }}
                            />
                            <Typography variant="caption" sx={{ color: "neutral.lighter" }}>
                                {o.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Toolbar: gráfico/tabela + ordenação */}
            <Grid container justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <TabFilters
                    filters={viewFilters}
                    selectedFilter={selectedViewFilter}
                    onFilterChange={(f) => setViewMode(f.id as ViewMode)}
                />
                <Grid container alignItems="center" gap={1} sx={{ width: "auto" }}>
                    <Typography variant="caption" color="quaternary">
                        {sortDirection === "desc"
                            ? intl("objectiveMonitoringSortHintDesc")
                            : intl("objectiveMonitoringSortHintAsc")}
                    </Typography>
                    <IconButton
                        size="small"
                        color="primary"
                        variant="clean"
                        aria-label={intl("objectiveMonitoringSortToggleAria")}
                        onClick={() => setSortDirection((d) => (d === "desc" ? "asc" : "desc"))}
                    >
                        <SwapVert fontSize="small" />
                    </IconButton>
                </Grid>
            </Grid>

            {/* Duas colunas: assessores (selecionáveis) | drill-down por oportunidade/carteira */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                    alignItems: "start",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "neutral.lighter", fontWeight: 700, display: "block" }}>
                        {intl("aggOpportunitiesByAdvisorTitle")}
                    </Typography>
                    <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 1 }}>
                        {intl("aggOpportunitiesByAdvisorHint")}
                    </Typography>
                    {viewMode === "chart" ? (
                        <AdvisorAggregateChart
                            rows={sortedAdvisors}
                            opportunityColors={model.opportunityColors}
                            selectedKeys={selectedAdvisors}
                            onToggle={toggleAdvisor}
                        />
                    ) : (
                        <AdvisorAggregateTable
                            rows={sortedAdvisors}
                            selectedKeys={selectedAdvisors}
                            onToggle={toggleAdvisor}
                            tableSx={tableSx}
                            tableContainerSx={tableContainerSx}
                        />
                    )}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: "neutral.lighter", fontWeight: 700, display: "block", mb: 1 }}
                    >
                        {intl("aggOpportunitiesDrilldownTitle")}
                    </Typography>
                    {selectedRows.length > 0 ? (
                        <AdvisorAggregateDrilldown
                            advisors={selectedRows}
                            opportunityColors={model.opportunityColors}
                            viewMode={viewMode}
                            tableSx={tableSx}
                            tableContainerSx={tableContainerSx}
                        />
                    ) : (
                        <Typography variant="caption" color="quaternary">
                            {intl("aggOpportunitiesDrilldownEmpty")}
                        </Typography>
                    )}
                </Box>
            </Box>
        </BoxCard>
    )
}

export { AggregatedOpportunitiesChart }
